import { useEffect, useRef } from "react";
import { FinaComputedConsentStatus } from "Shared/util/types/api";
import { categoriseConsents, findShortestConsentExpiry } from "../utils";
import { useAddConsentExpiredBanners } from "./useAddConsentExpiredBanners";
import { useAddConsentExpiresSoonBanners } from "./useAddConsentExpiresSoonBanners";
import { useAddConsentNotProvidedBanners } from "./useAddConsentNotProvidedBanners";
import { useGetDataForPayoutInformationBanners } from "./useGetDataForPayoutInformationBanners";
import { useGlobalNotificationBanner } from "Shared/util/custom-hooks";

export const useGlobalPayoutInformationBanners = () => {
    const { removeGlobalNotificationBanner } = useGlobalNotificationBanner();
    const addConsentExpiredBanners = useAddConsentExpiredBanners();
    const addConsentExpiresSoonBanners = useAddConsentExpiresSoonBanners();
    const addConsentNotProvidedBanners = useAddConsentNotProvidedBanners();
    const { bankConsents, customerData, shouldNotRenderBanners } =
        useGetDataForPayoutInformationBanners();
    const bannerIdsRef = useRef<string[] | null>(null);

    const cleanupBanners = () => {
        bannerIdsRef.current?.forEach(removeGlobalNotificationBanner);
        bannerIdsRef.current = null;
    };

    useEffect(() => {
        cleanupBanners();

        if (shouldNotRenderBanners || !bankConsents) {
            return;
        }

        const categorisedConsents = {
            notProvided: customerData?.isEnabledForCashFlowAnalysis
                ? categoriseConsents(
                      bankConsents,
                      FinaComputedConsentStatus.NotProvided,
                  )
                : [],
            expired: categoriseConsents(
                bankConsents,
                FinaComputedConsentStatus.Expired,
            ),
            expiresSoon: customerData?.hasActiveContract
                ? categoriseConsents(
                      bankConsents,
                      FinaComputedConsentStatus.ExpiresSoon,
                  )
                : [],
        };

        bannerIdsRef.current = [
            ...addConsentNotProvidedBanners(
                categorisedConsents.notProvided.map(
                    (consent) => consent.bank.name,
                ),
            ),
            ...addConsentExpiredBanners(
                categorisedConsents.expired.map((consent) => consent.bank.name),
            ),
            ...addConsentExpiresSoonBanners(
                categorisedConsents.expiresSoon.map((consent) => ({
                    name: consent.bank.name,
                    daysUntilExpiry: findShortestConsentExpiry(consent),
                })),
            ),
        ];

        return cleanupBanners;
    }, [
        addConsentExpiredBanners,
        addConsentExpiresSoonBanners,
        addConsentNotProvidedBanners,
        bankConsents,
        customerData?.isEnabledForCashFlowAnalysis,
        customerData?.hasActiveContract,
        shouldNotRenderBanners,
        removeGlobalNotificationBanner,
    ]);
};
